import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import classNames from "components/ui/classNames";
import * as React from "react";

const buttonVariants = cva(
  classNames(
    "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors",
    "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-indigo-100 focus-visible:ring-offset-1 focus-visible:ring-offset-indigo-300",
    "disabled:pointer-events-none disabled:opacity-50",
  ),
  {
    variants: {
      variant: {
        default: "bg-brand text-white hover:bg-brand/90",
        destructive: "bg-red-600 text-white hover:bg-red-600/90",
        success: "bg-emerald-600 text-white hover:bg-emerald-600/90",
        outline: "border border-input",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        link: "underline-offset-4 hover:underline text-brand",
        ghost: "",

        "primary/green": classNames(
          "bg-green-600 text-white",
          "dark:bg-green-800/60 dark:text-green-200 dark:hover:bg-green-600/50",
          "outline-none",
          "focus-visible:ring-2 focus-visible:ring-green-600 focus-visible:ring-offset-2 focus-visible:ring-offset-current",
          "dark:focus-visible:ring-0 dark:focus-visible:ring-green-800 dark:focus-visible:ring-offset-2 dark:focus-visible:ring-offset-green-400/40",
          "hover:opacity-80",
        ),

        "primary/cyan": classNames(
          "bg-cyan-600 text-white",
          "dark:bg-cyan-800/60 dark:text-cyan-200 dark:hover:bg-cyan-600/50",
          "outline-none",
          "focus-visible:ring-2 focus-visible:ring-cyan-600 focus-visible:ring-offset-2 focus-visible:ring-offset-current",
          "dark:focus-visible:ring-0 dark:focus-visible:ring-cyan-800 dark:focus-visible:ring-offset-2 dark:focus-visible:ring-offset-green-400/40",
          "hover:opacity-80",
        ),

        "primary/teal": classNames(
          "bg-teal-600 text-white",
          "dark:bg-teal-800/60 dark:text-teal-200 dark:hover:bg-teal-600/50",
          "outline-none",
          "focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2 focus-visible:ring-offset-current",
          "dark:focus-visible:ring-0 dark:focus-visible:ring-teal-800 dark:focus-visible:ring-offset-2 dark:focus-visible:ring-offset-green-400/40",
          "hover:opacity-80",
        ),
      },
      shape: {
        default: "rounded-md",
        capsule: "rounded-2xl",
      },
      size: {
        default: "h-10 py-2 px-4",
        sm: "h-8 px-3",
        lg: "h-11 px-8",
        full: "h-10 w-full",
      },
    },
    defaultVariants: {
      shape: "default",
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, shape, size, asChild = false, ...props }, ref) => {
    const Component = asChild ? Slot : "button";
    return (
      <Component className={classNames(buttonVariants({ variant, shape, size }), className)} ref={ref} {...props} />
    );
  },
);

Button.displayName = "Button";

export { Button, buttonVariants };
